import { useState, useEffect } from 'react';

// A reusable drag-to-reorder list using native HTML5 DnD.
// Drag is initiated ONLY from a handle: renderItem(item, dragHandleProps) must
// spread dragHandleProps onto a grip element. The row itself is a drop target but
// is NOT draggable — so a normal click on row contents never starts a drag (this
// avoids stuck drag-state / sticky :active highlight on click).
// onReorder(orderedIds) fires once on drop. enabled=false → plain list.
// intoIds/onDropInto: айтемы из intoIds — не цели пересортировки, а «контейнеры»:
// бросок на них вызывает onDropInto(draggedId, containerId) вместо reorder
// (используется: перетащить вкладку на «Моё» → раздел переезжает внутрь).
export default function DraggableList({ items, renderItem, onReorder, enabled = true, gap = 8, intoIds = [], onDropInto = null }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Два сценария залипания индикаторов: (1) элемент исчез из списка во время
  // drag (перенос в «Моё») — dragend по размонтированному узлу не приходит;
  // (2) drop за пределами окна — часть браузеров не шлёт dragend вовсе.
  const sig = items.map(i => i.id).join('|');
  useEffect(() => { setDragId(null); setOverId(null); }, [sig]);
  useEffect(() => {
    const clear = () => { setDragId(null); setOverId(null); };
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    // Абсолютная страховка: любой следующий клик/тап стирает индикаторы —
    // какие бы экзотические сценарии drag ни оставили их висеть.
    window.addEventListener('pointerdown', clear);
    return () => { window.removeEventListener('dragend', clear); window.removeEventListener('drop', clear); window.removeEventListener('pointerdown', clear); };
  }, []);

  if (!enabled) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap }}>{items.map(it => <div key={it.id}>{renderItem(it, {})}</div>)}</div>;
  }

  const reset = () => { setDragId(null); setOverId(null); };
  const commit = () => {
    if (dragId && overId && dragId !== overId) {
      if (onDropInto && intoIds.includes(overId)) {
        onDropInto(dragId, overId);
      } else {
        const ids = items.map(i => i.id);
        const from = ids.indexOf(dragId), to = ids.indexOf(overId);
        if (from > -1 && to > -1) { ids.splice(to, 0, ids.splice(from, 1)[0]); onReorder(ids); }
      }
    }
    reset();
  };

  const handleProps = (id) => ({
    draggable: true,
    onDragStart: (e) => { setDragId(id); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); } catch {} },
    onDragEnd: commit,
    style: { cursor: 'grab', touchAction: 'none' },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {items.map(it => (
        <div key={it.id}
          onDragOver={(e) => { if (!dragId) return; e.preventDefault(); if (overId !== it.id) setOverId(it.id); }}
          onDrop={(e) => { e.preventDefault(); commit(); }}
          style={{
            opacity: dragId === it.id ? 0.4 : 1,
            borderTop: overId === it.id && dragId && dragId !== it.id && !intoIds.includes(it.id) ? '2px solid var(--app-accent,#c9a24b)' : '2px solid transparent',
            outline: overId === it.id && dragId && dragId !== it.id && intoIds.includes(it.id) ? '2px solid var(--app-accent,#c9a24b)' : 'none',
            borderRadius: intoIds.includes(it.id) ? 10 : 0,
            transition: 'opacity .15s',
          }}>
          {renderItem(it, handleProps(it.id))}
        </div>
      ))}
    </div>
  );
}
